import { useState, useRef, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, Loader2, UserRoundCheck, Paperclip, X, FileText, File as FileIcon, AtSign } from "lucide-react";
import { formatDistanceToNow, format, isToday, isYesterday } from "date-fns";
import { de } from "date-fns/locale";
import {
  useBoardTaskComments,
  useCreateBoardTaskComment,
  uploadTaskCommentFile,
  type CommentAttachment,
  type BoardTaskComment,
} from "@/hooks/useBoardTaskComments";
import { useAdminMembers } from "@/hooks/useAdminMembers";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface TaskCommentsProps {
  taskId: string;
  taskTitle: string;
  /** Für diese Personen ist die Aufgabe ausgeblendet: Sie erscheinen nicht in der @-Auswahl. */
  ausgeblendet?: string[];
}

interface PendingMention {
  userId: string;
  displayName: string;
}

export function TaskComments({ taskId, taskTitle, ausgeblendet = [] }: TaskCommentsProps) {
  const { data: comments = [], isLoading } = useBoardTaskComments(taskId);
  const { data: adminMembers = [] } = useAdminMembers();
  const { data: currentUser } = useCurrentUser();
  const createComment = useCreateBoardTaskComment();
  const [content, setContent] = useState("");
  const [pendingFiles, setPendingFiles] = useState<CommentAttachment[]>([]);
  const [pendingMentions, setPendingMentions] = useState<PendingMention[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionAnchor, setMentionAnchor] = useState<number>(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll zum neuesten Kommentar
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [comments.length]);

  const mentionCandidates = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase().trim();
    return adminMembers
      .filter((m) => !ausgeblendet.includes(m.user_id))
      .filter((m) => !q || m.displayName.toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionQuery, adminMembers, ausgeblendet]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setContent(val);
    const cursor = e.target.selectionStart;
    // Suche letztes "@" vor dem Cursor ohne Whitespace dazwischen
    const before = val.slice(0, cursor);
    const atMatch = before.match(/(?:^|\s)@([\wÄÖÜäöüß-]*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionAnchor(cursor - atMatch[1].length - 1); // Position des @
      setActiveIdx(0);
    } else {
      setMentionQuery(null);
    }
  };

  const insertMention = (member: { user_id: string; displayName: string }) => {
    if (mentionQuery === null) return;
    const before = content.slice(0, mentionAnchor);
    const afterStart = mentionAnchor + mentionQuery.length + 1;
    const after = content.slice(afterStart);
    const token = `@${member.displayName}`;
    const next = `${before}${token} ${after}`;
    setContent(next);
    setPendingMentions((prev) =>
      prev.some((m) => m.userId === member.user_id)
        ? prev
        : [...prev, { userId: member.user_id, displayName: member.displayName }]
    );
    setMentionQuery(null);
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = before.length + token.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery !== null && mentionCandidates.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, mentionCandidates.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(mentionCandidates[activeIdx]);
        return;
      }
      if (e.key === "Escape") {
        setMentionQuery(null);
        return;
      }
    }
    // Cmd/Ctrl+Enter zum Senden
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setIsUploading(true);
    try {
      const uploads = await Promise.all(Array.from(files).map((f) => uploadTaskCommentFile(f)));
      setPendingFiles((prev) => [...prev, ...uploads]);
    } catch {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePendingFile = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = () => {
    if (!content.trim() && pendingFiles.length === 0) return;
    // Nur Erwähnungen behalten, deren Token noch im Text steht
    const usedMentions = pendingMentions
      .filter((m) => content.includes(`@${m.displayName}`))
      .map((m) => m.userId);

    createComment.mutate(
      {
        taskId,
        taskTitle,
        content: content.trim() || (pendingFiles.length > 0 ? "📎 Anhang" : ""),
        attachments: pendingFiles,
        mentions: usedMentions,
      },
      {
        onSuccess: () => {
          setContent("");
          setPendingFiles([]);
          setPendingMentions([]);
        },
      }
    );
  };

  const getInitials = (firstName: string | null, lastName: string | null) =>
    `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "?";

  const getName = (firstName: string | null, lastName: string | null) =>
    [firstName, lastName].filter(Boolean).join(" ") || "Unbekannt";

  const dateSeparator = (d: Date) => {
    if (isToday(d)) return "Heute";
    if (isYesterday(d)) return "Gestern";
    return format(d, "EEEE, d. MMMM yyyy", { locale: de });
  };

  // Kommentare mit Tages-Separator
  const grouped = useMemo(() => {
    const result: Array<{ type: "sep"; key: string; label: string } | { type: "c"; key: string; comment: BoardTaskComment }> = [];
    let lastDay = "";
    for (const c of comments) {
      const d = new Date(c.created_at);
      const day = format(d, "yyyy-MM-dd");
      if (day !== lastDay) {
        result.push({ type: "sep", key: `sep-${day}`, label: dateSeparator(d) });
        lastDay = day;
      }
      result.push({ type: "c", key: c.id, comment: c });
    }
    return result;
  }, [comments]);

  const renderContentWithMentions = (text: string) => {
    // Erwähnungen optisch hervorheben: @Name (bekannter Admin)
    const names = adminMembers.map((m) => m.displayName).sort((a, b) => b.length - a.length);
    if (names.length === 0) return text;
    const parts: Array<{ text: string; isMention: boolean }> = [];
    let rest = text;
    while (rest.length > 0) {
      let matched = false;
      for (const name of names) {
        const token = `@${name}`;
        const idx = rest.indexOf(token);
        if (idx === 0) {
          parts.push({ text: token, isMention: true });
          rest = rest.slice(token.length);
          matched = true;
          break;
        }
      }
      if (!matched) {
        // nächstes @ finden
        const nextAt = rest.indexOf("@", 1);
        if (nextAt === -1) {
          parts.push({ text: rest, isMention: false });
          rest = "";
        } else {
          parts.push({ text: rest.slice(0, nextAt), isMention: false });
          rest = rest.slice(nextAt);
        }
      }
    }
    return parts.map((p, i) =>
      p.isMention ? (
        <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-primary/15 text-primary font-semibold text-[13px]">
          {p.text}
        </span>
      ) : (
        <span key={i}>{p.text}</span>
      )
    );
  };

  return (
    <div className="space-y-3 flex flex-col h-full">
      {/* Verlauf */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-[280px] max-h-[380px] overflow-y-auto rounded-xl bg-background/70 border border-primary/15 p-3 space-y-3 shadow-inner"
      >
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : grouped.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
              <span className="text-2xl">💬</span>
            </div>
            <p className="text-sm text-muted-foreground">Noch keine Nachrichten.</p>
            <p className="text-xs text-muted-foreground mt-1">Starte den Austausch zu dieser Aufgabe.</p>
          </div>
        ) : (
          grouped.map((item) => {
            if (item.type === "sep") {
              return (
                <div key={item.key} className="flex items-center gap-2 my-2">
                  <div className="flex-1 h-px bg-primary/15" />
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{item.label}</span>
                  <div className="flex-1 h-px bg-primary/15" />
                </div>
              );
            }
            const comment = item.comment;
            const isMine = comment.user_id === currentUser?.id;
            const wasMentioned = comment.mentions?.includes(currentUser?.id || "");
            return (
              <div key={item.key} className={cn("flex gap-2.5", isMine && "flex-row-reverse")}>
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className={cn("text-[11px] font-semibold", isMine ? "bg-primary text-primary-foreground" : "bg-accent/20 text-accent-foreground")}>
                    {getInitials(comment.author?.first_name ?? null, comment.author?.last_name ?? null)}
                  </AvatarFallback>
                </Avatar>
                <div className={cn("flex-1 min-w-0", isMine && "flex flex-col items-end")}>
                  <div className={cn("flex items-baseline gap-2 mb-1", isMine && "flex-row-reverse")}>
                    <span className="font-semibold text-xs text-foreground">
                      {isMine ? "Du" : getName(comment.author?.first_name ?? null, comment.author?.last_name ?? null)}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true, locale: de })}
                    </span>
                  </div>
                  <div
                    className={cn(
                      "inline-block max-w-full rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words shadow-sm",
                      isMine
                        ? "bg-primary text-primary-foreground rounded-tr-sm"
                        : wasMentioned
                          ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-foreground rounded-tl-sm"
                          : "bg-muted/80 text-foreground rounded-tl-sm"
                    )}
                  >
                    {renderContentWithMentions(comment.content)}
                  </div>
                  {wasMentioned && !isMine && (
                    <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-700 dark:text-amber-400">
                      <AtSign className="h-2.5 w-2.5" /> Du wurdest erwähnt
                    </div>
                  )}
                  {comment.attachments?.length > 0 && (
                    <div className={cn("flex flex-wrap gap-2 mt-2", isMine && "justify-end")}>
                      {comment.attachments.map((att, idx) => (
                        <AttachmentDisplay key={idx} attachment={att} />
                      ))}
                    </div>
                  )}
                  {comment.reassigned_to && comment.reassigned_to_profile && (
                    <div className="flex items-center gap-1 mt-1.5 text-[11px] text-accent-foreground bg-accent/15 rounded-full px-2 py-0.5 w-fit">
                      <UserRoundCheck className="h-3 w-3" />
                      Zugewiesen an {getName(comment.reassigned_to_profile.first_name, comment.reassigned_to_profile.last_name)}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pending files preview */}
      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {pendingFiles.map((file, idx) => (
            <div key={idx} className="relative group rounded-lg border bg-muted/50 overflow-hidden flex items-center gap-2 px-2 py-1.5 text-xs">
              {file.type === "image" ? (
                <img src={file.url} alt={file.name} className="w-10 h-10 object-cover rounded" />
              ) : file.type === "pdf" ? (
                <FileText className="h-4 w-4 text-red-500 shrink-0" />
              ) : (
                <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <span className="truncate max-w-[100px]">{file.name}</span>
              <Button variant="ghost" size="icon" className="h-5 w-5 p-0 shrink-0" onClick={() => removePendingFile(idx)}>
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Eingabe */}
      <div className="relative rounded-xl border-2 border-primary/30 bg-background shadow-sm focus-within:border-primary transition-colors">
        <div className="relative">
          <Textarea
            ref={textareaRef}
            placeholder="Nachricht schreiben…  (@ um jemanden zu erwähnen)"
            value={content}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            className="min-h-[80px] resize-none text-sm border-0 focus-visible:ring-0 shadow-none rounded-none bg-transparent"
          />
          {/* Mention popover */}
          {mentionQuery !== null && mentionCandidates.length > 0 && (
            <div className="absolute left-3 bottom-full mb-2 z-50 w-64 rounded-lg border bg-popover shadow-lg overflow-hidden">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/50 border-b">
                Person erwähnen
              </div>
              <div className="max-h-56 overflow-y-auto">
                {mentionCandidates.map((m, i) => (
                  <button
                    key={m.user_id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(m);
                    }}
                    onMouseEnter={() => setActiveIdx(i)}
                    className={cn(
                      "flex items-center gap-2 w-full px-3 py-2 text-left text-sm hover:bg-primary/10 transition-colors",
                      i === activeIdx && "bg-primary/10"
                    )}
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-[10px] bg-primary/15 text-primary font-semibold">
                        {getInitials(m.first_name, m.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{m.displayName}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-2 py-1.5 bg-muted/30 border-t">
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 gap-1 text-xs text-muted-foreground hover:text-primary"
              onClick={() => {
                const ta = textareaRef.current;
                if (!ta) return;
                const pos = ta.selectionStart;
                const next = `${content.slice(0, pos)}@${content.slice(pos)}`;
                setContent(next);
                requestAnimationFrame(() => {
                  ta.focus();
                  ta.setSelectionRange(pos + 1, pos + 1);
                  setMentionQuery("");
                  setMentionAnchor(pos);
                });
              }}
              title="Person erwähnen"
            >
              <AtSign className="h-3.5 w-3.5" />
              Erwähnen
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 gap-1 text-xs text-muted-foreground hover:text-primary"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
              title="Datei anhängen"
            >
              {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
              Anhang
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              onChange={handleFileSelect}
            />
          </div>
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={(!content.trim() && pendingFiles.length === 0) || createComment.isPending}
            className="gap-1.5 h-8 px-4"
          >
            {createComment.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            Senden
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Display a single attachment inline */
function AttachmentDisplay({ attachment }: { attachment: CommentAttachment }) {
  if (attachment.type === "image") {
    return (
      <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={attachment.url}
          alt={attachment.name}
          className="max-w-[200px] max-h-[150px] rounded-md border object-cover cursor-pointer hover:opacity-90 transition-opacity"
        />
      </a>
    );
  }
  const Icon = attachment.type === "pdf" ? FileText : FileIcon;
  const iconColor = attachment.type === "pdf" ? "text-red-500" : "text-muted-foreground";
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1.5 px-2 py-1 rounded border bg-background hover:bg-muted/50 transition-colors text-xs"
    >
      <Icon className={`h-3.5 w-3.5 shrink-0 ${iconColor}`} />
      <span className="truncate max-w-[140px]">{attachment.name}</span>
    </a>
  );
}
